import {
	Component,
	OnInit,
	ViewEncapsulation,
	Input,
	Output,
	EventEmitter,
	forwardRef,
	ViewChild,
	ElementRef,
	OnDestroy,
	SimpleChanges,
	OnChanges,
	HostListener,
	ChangeDetectorRef
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { IdService, IGenericSelectOption, FormControlBase } from '../../shared/index';

@Component({
	selector: 'rbc-multiselect',
	templateUrl: './multiselect.component.html',
	encapsulation: ViewEncapsulation.None,
	styleUrls: ['./multiselect.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(() => MultiselectComponent)
		}
	],
	host: {
		'(blur)': 'touch()'
	}
})
export class MultiselectComponent extends FormControlBase<IGenericSelectOption[]> implements OnInit, OnDestroy, OnChanges {
	@Input() checkedText: string = 'checked';
	@Input() disabled: boolean = false;
	@Input() displayTotal: boolean = false;
	@Input() height: string;
	@Input() helpText: string;
	@Input() id: string;
	@Input() label: string;
	@Input() options: IGenericSelectOption[];
	@Input() model: IGenericSelectOption[];
	@Input() placeholder: string = 'Choose';
	@Input() size: string;
	@Input() sizeWidth: string;
	@Input() ariaLabel: string;
	@Input() ariaLabelledby: string;
	@Input() ariaDescribedby: string;

	@Output() selectUpdate = new EventEmitter<IGenericSelectOption[]>();
	@Output() modelChange = new EventEmitter<IGenericSelectOption[]>();
	@Output() dropdownClosed = new EventEmitter<IGenericSelectOption[]>();

	@ViewChild('dropdown') $dropdown: ElementRef;
	@ViewChild('selections') $selections: ElementRef;
	@ViewChild('cardResults') $cardResults: ElementRef;

	selectedLabel: string;
	selected: string;
	open: boolean = false;
	highlightID: string;
	index: number = 0;

	@HostListener('document:click', ['$event'])
	clickout(event) {
		if (!this.eRef.nativeElement.contains(event.target) && this.open) {
			this.$_SelectDropdown_close();
			this.cdr.markForCheck();
		}
	}

	@HostListener('document:touchend', ['$event'])
	touchend(event) {
		if (!this.eRef.nativeElement.contains(event.target) && this.open) {
			this.$_SelectDropdown_close();
			this.cdr.markForCheck();
		}
	}

	constructor(private idService: IdService, private eRef: ElementRef, private cdr: ChangeDetectorRef) {
		super();
		this.id = idService.generate();
	}

	ngOnInit() {
		// this.options = this.options ? this.options : this.model ? this.model : [];
		this.checkedText = this.checkedText.trim() ? this.checkedText : 'checked';
		// this.emitState();
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
		super.ngOnDestroy();
	}

	ngOnChanges(changes: SimpleChanges) {
		super.ngOnChanges(changes);

		if (changes && changes.model && this.options) {
			this.setSelectedOptions(changes.model.currentValue);
		}
	}

	setSelectedOptions(options: IGenericSelectOption[]) {
		if (options) {
			this.options.forEach(o => {
				if (options.find(m => m.selected && m.value === o.value)) {
					o.selected = true;
				}
			});
		} else {
			this.options.forEach(o => (o.selected = false));
		}
	}

	classGen() {
		return {
			[`multiselect-${this.size}`]: this.size && (this.size === 'large' || this.size === 'small'),
			[`multiselect-width-${this.sizeWidth}`]: this.sizeWidth,
			disabled: this.disabled && !this.disabled
		};
	}

	selectedOptionsLabel() {
		return this.options
			.filter(el => el.selected)
			.map(el => {
				return el.label;
			})
			.join(', ');
	}

	// Creates the formatted label on the multiselect
	selectedLabelFormatted() {
		const nbSelected = this.options.filter(option => option.selected).length;
		const total = this.displayTotal ? ' / ' + this.options.length : '';
		return nbSelected > 0 ? nbSelected + total + ' ' + this.checkedText : this.placeholder;
	}

	writeValue(options: IGenericSelectOption[]): void {
		this.setSelectedOptions(options);
		this.innerValue = this.options.filter(o => o.selected);
	}

	// remove the event listener if null prop, or target is not the dropdown and not one of list elements
	$_SelectDropdown_close() {
		this.open = false;
		this.dropdownClosed.emit(this.innerValue);
	}

	$_Multiselect_open(e = null) {
		this.open = !this.open && !(this.disabled && !this.disabled);
		// if menu currently open don't add another event handler
		if (!this.open) {
			this.$_SelectDropdown_close();
		}
	}

	updateSelected(value, fromKeyPress) {
		this.index = this.options.findIndex(option => option.value === value);
		if (fromKeyPress) {
			this.options[this.index].selected = !this.options[this.index].selected;
		}
		this.setModel(this.options.filter(o => o.selected));
		this.emitState();
	}

	emitState() {
		this.modelChange.emit(this.innerValue);
		this.selectUpdate.emit(this.innerValue);
	}

	$_SelectDropdown_nextEligibleOption(index, options, nbOptions) {
		let nextIndex = index + 1;
		while (nextIndex < nbOptions && options[nextIndex].disabled) {
			nextIndex += 1;
		}
		// if next index was not past end return it else return original
		return nextIndex !== nbOptions ? nextIndex : index;
	}

	$_SelectDropdown_previousEligibleOption(index, options) {
		let nextIndex = index - 1;
		while (nextIndex > -1 && options[nextIndex].disabled) {
			nextIndex -= 1;
		}
		// if next index was not past end return it else return original
		return nextIndex > -1 ? nextIndex : index;
	}

	$_SelectDropdown_updateHighlight(e) {
		const key = e.key.toLowerCase();
		const options = this.options;
		const nbOptions = options.length;
		if (nbOptions > 0 && this.open) {
			// if key press down
			if (key.indexOf('down') > -1) {
				e.preventDefault();
				this.index = this.$_SelectDropdown_nextEligibleOption(this.index, this.options, nbOptions);
				this.highlightID = options[this.index].id;
			} else if (key.indexOf('up') > -1) {
				e.preventDefault();
				this.index = this.$_SelectDropdown_previousEligibleOption(this.index, this.options);
				this.highlightID = options[this.index].id;
			} else if (key === 'enter' && !this.options[this.index].disabled) {
				e.preventDefault();
				this.updateSelected(this.options[this.index].value, true);
				this.$_SelectDropdown_close(); // Remove this line to stop enter from closing multiselect
			} else if (key === 'escape' || key === ' ') {
				e.preventDefault();
				this.open = false;
				this.$_SelectDropdown_close();
			}
		} else if (key.indexOf('down') > -1 || key.indexOf('up') > -1 || key === 'enter' || key === ' ') {
			e.preventDefault();
			this.$_Multiselect_open();
		}
	}
}
